import React from 'react';

import { cssMap } from '@atlaskit/css';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';

import { CardContent as LegacyCardContent } from './Legacy/CardContent';

export type CardContentProps = React.ComponentProps<typeof LegacyCardContent> & {
	/** ID for associating the visible description with the card's primary action. */
	descriptionId?: string;
	/** Applies disabled text colors in the uplifted card. */
	isDisabled?: boolean;
};

const styles = cssMap({
	content: {
		gridArea: 'content',
		alignSelf: 'start',
		minWidth: 0,
	},
	title: { minWidth: 0 },
	badge: { flexShrink: 0 },
	actions: { position: 'relative', zIndex: 200, flexShrink: 0 },
});

export const CardContent = (props: CardContentProps): React.JSX.Element => {
	if (!expVal('jira_browse_agent_modal_update', 'isEnabled', false)) {
		return <LegacyCardContent {...props} />;
	}

	const { name, description, descriptionId, badge, headerActions, subtitle, isDisabled, testId } =
		props;

	return (
		<Stack xcss={styles.content} space="space.150" testId={testId}>
			<Stack>
				<Inline space="space.100" alignBlock="center" spread="space-between">
					<Inline xcss={styles.title} space="space.100" alignBlock="center">
						<Text
							size="large"
							weight="medium"
							color={isDisabled ? 'color.text.disabled' : 'color.text'}
							maxLines={1}
						>
							{name}
						</Text>
						{badge ? <Box xcss={styles.badge}>{badge}</Box> : null}
					</Inline>
					{headerActions ? <Box xcss={styles.actions}>{headerActions}</Box> : null}
				</Inline>
				{subtitle ? (
					<Text size="small" color={isDisabled ? 'color.text.disabled' : 'color.text.subtlest'}>
						{subtitle}
					</Text>
				) : null}
			</Stack>
			{description ? (
				<Text
					id={descriptionId}
					size="small"
					color={isDisabled ? 'color.text.disabled' : 'color.text.subtlest'}
					maxLines={3}
				>
					{description}
				</Text>
			) : null}
		</Stack>
	);
};
